import {
  REGISTRATION_FLOWS,
  type RegistrationFlow,
} from '@repo/api-kit/queries/registration-states';
import {
  AT_EINZELUNTERNEHMER_LEGAL_CODE,
  COMPANY_STATUS,
  GERMAN_FREELANCER_LEGAL_FORMS,
  type OrganizationType,
  PORTUGUESE_EXISTING_FREELANCERS,
  UG_IG_LEGAL_CODE,
} from '../../organizations/constants';

export interface InitialFlowOrganization {
  initialOrganizationType: OrganizationType | null;
  legalFormCreditsafeCode: string | null;
}

export function buildUrl(path: string, queryParams: Record<string, string | boolean>): string {
  const queryEntries = Object.entries(queryParams);

  if (queryEntries.length === 0) {
    return path;
  }

  const queryString = queryEntries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
    .join('&');

  return `${path}?${queryString}`;
}

const SIGNUP_SELECT_COUNTRY_FLOWS = new Set<RegistrationFlow>([
  REGISTRATION_FLOWS.FR_GLOBAL,
  REGISTRATION_FLOWS.FR_EXISTING_COMPANY,
  REGISTRATION_FLOWS.FR_COMPANY_CREATION,
  REGISTRATION_FLOWS.FR_FULL_COMPANY_CREATION_WITH_CAPITAL_DEPOSIT,
  REGISTRATION_FLOWS.FR_FULL_COMPANY_CREATION_WITHOUT_CAPITAL_DEPOSIT,
  REGISTRATION_FLOWS.FR_WORKS_COUNCIL,
  REGISTRATION_FLOWS.NL_COMPANY_CREATION,
]);

export function getInitialFlowMapping(
  registrationFlow: RegistrationFlow,
  organization: InitialFlowOrganization,
  isMobileRegister = false
): string {
  const legalFormCreditsafeCode = organization.legalFormCreditsafeCode ?? '';

  if (SIGNUP_SELECT_COUNTRY_FLOWS.has(registrationFlow) || isMobileRegister) {
    return '/signup/select-country';
  }
  if (registrationFlow === REGISTRATION_FLOWS.DE_COMPANY_CREATION) {
    return buildUrl('signup/company-creation/type', {
      country: 'DE',
      legal_form_creditsafe_code: UG_IG_LEGAL_CODE,
      status: COMPANY_STATUS.UNREGISTERED,
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.DE_EXISTING_COMPANY) {
    // oxlint-disable-next-line typescript/consistent-type-assertions
    const isFreelancerInGermany = (GERMAN_FREELANCER_LEGAL_FORMS as readonly string[]).includes(
      legalFormCreditsafeCode
    );

    if (isFreelancerInGermany) {
      return buildUrl('signup/select-legal-form', {
        country: 'DE',
      });
    }

    return buildUrl('signup/select-employees', {
      country: 'DE',
      status: COMPANY_STATUS.REGISTERED,
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.DE_FULL_COMPANY_CREATION_FREELANCERS) {
    return buildUrl('signup/select-legal-form-group', {
      country: 'DE',
      status: COMPANY_STATUS.UNREGISTERED,
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.DE_COMPANY_CREATION_SOLE_TRADER) {
    return buildUrl('signup/select-legal-form-group', {
      country: 'DE',
      status: COMPANY_STATUS.UNREGISTERED,
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.DE_FULL_COMPANY_CREATION_COMPANIES) {
    return buildUrl('signup/company-creation/type', {
      country: 'DE',
      legal_form_creditsafe_code: UG_IG_LEGAL_CODE,
      status: COMPANY_STATUS.UNREGISTERED,
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.ES_COMPANY_CREATION) {
    return buildUrl('signup/select-status', {
      country: 'ES',
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.IT_COMPANY_CREATION) {
    return buildUrl('signup/select-status', {
      country: 'IT',
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.ES_EXISTING_COMPANY) {
    return buildUrl('signup/select-employees', {
      country: 'ES',
      status: COMPANY_STATUS.REGISTERED,
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.IT_EXISTING_COMPANY) {
    return buildUrl('signup/select-employees', {
      country: 'IT',
      status: COMPANY_STATUS.REGISTERED,
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.NL_EXISTING_COMPANY) {
    return buildUrl('signup/select-employees', {
      country: 'NL',
      status: COMPANY_STATUS.REGISTERED,
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.BE_EXISTING_COMPANY) {
    return buildUrl('signup/select-employees', {
      country: 'BE',
      status: COMPANY_STATUS.REGISTERED,
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.AT_EXISTING_COMPANY) {
    if (legalFormCreditsafeCode === AT_EINZELUNTERNEHMER_LEGAL_CODE) {
      return buildUrl('signup/select-legal-form', {
        country: 'AT',
        status: COMPANY_STATUS.REGISTERED,
      });
    }
    return buildUrl('signup/select-employees', {
      country: 'AT',
      status: COMPANY_STATUS.REGISTERED,
      legal_form_creditsafe_code: legalFormCreditsafeCode,
    });
  } else if (registrationFlow === REGISTRATION_FLOWS.PT_EXISTING_COMPANY) {
    if (PORTUGUESE_EXISTING_FREELANCERS.includes(legalFormCreditsafeCode)) {
      return buildUrl('signup/select-legal-form-group', {
        country: 'PT',
        status: COMPANY_STATUS.REGISTERED,
      });
    }
    return buildUrl('signup/select-employees', {
      country: 'PT',
      status: COMPANY_STATUS.REGISTERED,
      legal_form_creditsafe_code: legalFormCreditsafeCode,
    });
  }
  throw new Error(`No initial flow mapping found for registration flow: ${registrationFlow}`);
}
